import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";

import { EmptyState } from "@/components/dashboard/data-table/empty-state";
import { FilterBar } from "@/components/dashboard/data-table/filter-bar";
import { Pagination } from "@/components/dashboard/data-table/pagination";
import { SortHeader } from "@/components/dashboard/data-table/sort-header";
import {
  TBody,
  TD,
  TEmpty,
  TH,
  THead,
  TR,
  Table,
  TableShell,
} from "@/components/dashboard/data-table/table";
import { PageHeader } from "@/components/dashboard/page-header";
import { RowCheckbox } from "@/components/dashboard/data-table/selection";
import { EmployeeStatusPill } from "@/components/employees/account-status";
import { PersonAvatar } from "@/components/employees/person-avatar";
import { BulkActions } from "@/components/employees/bulk-actions";
import { RowActions } from "@/components/employees/row-actions";
import { employees as copy, nav, roles } from "@/content";
import { ROLES, can, scopeToSupervisorId } from "@/constants/permissions";
import { useQuery } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useFmt } from "@/hooks/shared/useFmt";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useSession } from "@/context/UserContext";
import {
  DEFAULT_PAGE_SIZE,
  buildQuery,
  readEnum,
  readNumber,
  readPage,
  readPageSize,
  readSearch,
  readSort,
  emptyPage,
} from "@/utils/query-params";
import {
  EMPLOYEE_SORT_COLUMNS,
  listEmployees,
  listSupervisors,
  listZoneOptions,
} from "@/api/employees";
import { listCompanyOptions } from "@/utils/services/companies";

const BASE_PATH = "/dashboard/employees";
const WAITING_LIST_PATH = "/dashboard/waiting-list";

export const Route = createFileRoute("/_main/dashboard/employees/")({
  component: () => (
    <RequirePermission action="employees.read">
      <EmployeesPage />
    </RequirePermission>
  ),
});

function EmployeesPage() {
  useDocumentTitle(copy.title);
  const { user } = useSession();
  const params = useQueryParams();
  const fmt = useFmt();

  const sort = readSort(params, EMPLOYEE_SORT_COLUMNS, "name");
  const page = readPage(params);
  const pageSize = readPageSize(params);

  const query = {
      search: readSearch(params),
      role: readEnum(params, "role", ROLES),
      /* Active staff directory — pending / waiting / suspended live on Waiting List. */
      status: "active" as const,
      zoneId: readNumber(params, "zone"),
      supervisorId: readNumber(params, "supervisor"),
      companyId: readNumber(params, "company"),
      department: typeof params.department === "string" ? params.department : undefined,
      workCity: typeof params.city === "string" ? params.city : undefined,
      /* A supervisor sees their own team. Derived from the permissions module so
         the rule is stated once, not restated per query — the prototype
         hardcoded it in one model function and forgot it in another. */
    scopeToSupervisorId: scopeToSupervisorId(user),
    sort,
    page,
    pageSize,
  };

  const list = useQuery({
    queryKey: ["employees", "list", query],
    queryFn: () => listEmployees(query),
  });

  /* The two option lists barely change and are shared by the filter bar and the
     bulk zone dialog, so they get their own long-lived keys rather than being
     refetched whenever a filter moves. */
  const supervisors = useQuery({
    queryKey: ["employees", "supervisors"],
    queryFn: listSupervisors,
    staleTime: 5 * 60_000,
  });
  const zones = useQuery({
    queryKey: ["employees", "zoneOptions"],
    queryFn: listZoneOptions,
    staleTime: 5 * 60_000,
  });
  const companies = useQuery({
    queryKey: ["companies", "options"],
    queryFn: listCompanyOptions,
    staleTime: 5 * 60_000,
    enabled: can(user.role, "companies.read"),
  });

  const result = list.data ?? emptyPage<never>();
  const supervisorOptions = supervisors.data ?? [];
  const zoneOptions = zones.data ?? [];
  const companyOptions = companies.data ?? [];

  const canCreate = can(user.role, "employees.create");
  const isFiltered = Boolean(
    readSearch(params) ||
      params.role ||
      params.zone ||
      params.supervisor ||
      params.company ||
      params.department ||
      params.city,
  );

  /* Actions column only when the reader can actually do something. A column of
     empty cells is worse than no column. */
  const canApprove = can(user.role, "employees.approve");
  const showActions =
    can(user.role, "employees.update") ||
    can(user.role, "employees.deactivate") ||
    canApprove;
  /* Bulk actions ride on two different permissions, because they are two
     different decisions: offboarding is the admin's, staffing a post is the
     supervisor's. `permissions.ts` holds the split; this only reads it. */
  const canDeactivate = can(user.role, "employees.deactivate");
  const canAssignZones = can(user.role, "zones.assignGuards");
  const canMessage = can(user.role, "employees.message");
  const showBulk = canDeactivate || canAssignZones || canMessage;

  /* What each action would actually change, computed from the rows on screen.
     Used to disable a checkbox on a row no action can touch, and to state the
     skip count in the confirmation before anything runs. The API recomputes
     all of these from the session — this decides what to draw, not what is
     allowed.

     Read from `accountStatus`, not from `active`: a pending hire and a rejected
     one are both `active: false`, and neither is something bulk reinstate should
     offer to act on. The bulk operations still only ever move a row between
     `active` and `suspended`. */
  const deactivatable = result.rows
    .filter((row) => row.accountStatus === "active" && row.id !== user.id)
    .map((row) => row.id);
  const reactivatable = result.rows
    .filter((row) => row.accountStatus === "suspended")
    .map((row) => row.id);
  const assignable = result.rows
    .filter((row) => row.role === "guard" && row.accountStatus === "active")
    .map((row) => row.id);
  const smsable = result.rows
    .filter((row) => Boolean(row.phone?.trim()))
    .map((row) => row.id);
  const emailable = result.rows
    .filter((row) => Boolean(row.email?.trim()))
    .map((row) => row.id);
  /* Push and in-app notifications reach the mobile workforce only. Admins and
     client accounts have no app, and the sender is never a recipient. */
  const notifiable = result.rows
    .filter(
      (row) =>
        (row.role === "guard" || row.role === "supervisor") && row.id !== user.id,
    )
    .map((row) => row.id);

  /* A row no permitted action can act on gets a disabled checkbox rather than
     none, so the column does not jump about while paging through mixed states. */
  const selectable = new Set([
    ...(canDeactivate ? [...deactivatable, ...reactivatable] : []),
    ...(canAssignZones ? assignable : []),
    ...(canMessage ? [...smsable, ...emailable, ...notifiable] : []),
  ]);

  const columnCount = 9 + (showActions ? 1 : 0) + (showBulk ? 1 : 0);

  const table = (
        <TableShell className="mt-5">
          <Table>
            <THead>
              {showBulk ? <TH srOnly>{copy.selectAll}</TH> : null}
              <SortHeader
                column="employeeNo"
                label={copy.columnEmployeeNo}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <SortHeader
                column="name"
                label={copy.columnName}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <TH>{copy.columnJobTitle}</TH>
              <SortHeader
                column="role"
                label={copy.columnRole}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <TH>{copy.columnSupervisor}</TH>
              <TH numeric>{copy.columnZones}</TH>
              <SortHeader
                column="status"
                label={copy.columnStatus}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <SortHeader
                column="createdAt"
                label={copy.columnCreated}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <TH>{copy.columnLastLogin}</TH>
              {showActions ? <TH srOnly>{copy.actionsLabel}</TH> : null}
            </THead>

            <TBody>
              {result.rows.length === 0 ? (
                <TEmpty colSpan={columnCount}>
                  <EmptyState
                    variant={isFiltered ? "filtered" : "empty"}
                    title={isFiltered ? copy.emptyFilteredTitle : copy.emptyTitle}
                    lead={isFiltered ? copy.emptyFilteredLead : copy.emptyLead}
                    actionHref={
                      isFiltered
                        ? BASE_PATH
                        : canCreate
                          ? WAITING_LIST_PATH
                          : undefined
                    }
                    actionLabel={
                      isFiltered
                        ? copy.clearFilters
                        : canCreate
                          ? copy.openWaitingList
                          : undefined
                    }
                  />
                </TEmpty>
              ) : (
                result.rows.map((row) => (
                  /* Only dim seats that are out of service. Pending / waiting-list
                     hires are `active: false` too, but they still need a decision —
                     muting them made "Awaiting approval" rows look broken. */
                  <TR
                    key={row.id}
                    muted={
                      row.accountStatus === "suspended" ||
                      row.accountStatus === "rejected"
                    }
                  >
                    {showBulk ? (
                      <TD className="w-10">
                        <RowCheckbox
                          id={row.id}
                          label={row.name}
                          disabled={!selectable.has(row.id)}
                        />
                      </TD>
                    ) : null}

                    <TD>
                      <span className="tabular text-xs font-semibold text-n8" dir="ltr">
                        {row.employeeNo ?? copy.noPhone}
                      </span>
                    </TD>

                    <TD>
                      <div className="flex items-center gap-3">
                        <PersonAvatar name={row.name} photoUrl={row.photoUrl} />
                        <div className="min-w-0">
                          <Link
                            to={`${BASE_PATH}/${row.id}`}
                            className="font-semibold text-n9 transition-colors duration-200 ease-brand hover:text-orange-700"
                          >
                            {row.name}
                          </Link>
                          <span className="mt-0.5 block text-xs text-n6">
                            {row.jobTitle ?? roles[row.role]}
                          </span>
                          <span className="mt-0.5 block text-xs text-n6" dir="ltr">
                            {row.email}
                          </span>
                        </div>
                      </div>
                    </TD>

                    <TD>
                      <span className="text-xs text-n7">{row.jobTitle ?? copy.noPhone}</span>
                    </TD>

                    <TD>
                      <span className="text-xs font-semibold text-n7">{roles[row.role]}</span>
                    </TD>

                    <TD>
                      <span className="text-xs text-n7">
                        {row.supervisorName ?? copy.noPhone}
                      </span>
                    </TD>

                    <TD numeric>
                      {row.zoneCount > 0 ? (
                        fmt.count(row.zoneCount)
                      ) : (
                        <span className="text-xs text-n6">{copy.noZones}</span>
                      )}
                    </TD>

                    <TD>
                      {/* Six distinct states, not two. "Invited but never
                          activated" is the one the prototype could not express, and
                          it is exactly what an admin needs to see to know a resend
                          is required; "waiting for approval" and "on the queue" are
                          the ones the hiring workflow added. */}
                      <EmployeeStatusPill
                        accountStatus={row.accountStatus}
                        passwordSetAt={row.passwordSetAt}
                        waitingQueuePosition={row.waitingQueuePosition}
                      />
                    </TD>

                    <TD numeric>
                      <span className="text-xs">{fmt.dateNumeric(row.createdAt)}</span>
                    </TD>

                    <TD>
                      <span className="text-xs text-n6">
                        {row.lastLoginAt ? fmt.since(row.lastLoginAt) : copy.neverSignedIn}
                      </span>
                    </TD>

                    {showActions ? (
                      <TD className="text-end">
                        <RowActions
                          id={row.id}
                          name={row.name}
                          role={row.role}
                          accountStatus={row.accountStatus}
                          pendingInvite={row.passwordSetAt === null}
                          canUpdate={can(user.role, "employees.update")}
                          canDeactivate={canDeactivate}
                          canInvite={can(user.role, "employees.invite")}
                          canApprove={canApprove}
                          isSelf={row.id === user.id}
                        />
                      </TD>
                    ) : null}
                  </TR>
                ))
              )}
            </TBody>
          </Table>

          {result.rows.length > 0 ? (
            <Pagination
              page={result.page}
              pageCount={result.pageCount}
              pageSize={result.pageSize}
              total={result.total}
              params={params}
              basePath={BASE_PATH}
              numerals={user.numeralSystem}
            />
          ) : null}
        </TableShell>
  );

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.people}
        title={copy.title}
        lead={copy.lead}
        actions={
          <Link
            to={WAITING_LIST_PATH}
            className="inline-flex h-10 items-center rounded-lg border border-n4 bg-n0 px-4 text-sm font-semibold text-n8 transition-colors duration-200 ease-brand hover:bg-n2"
          >
            {copy.openWaitingList}
          </Link>
        }
      />

      <div className="mt-6">
        <FilterBar
          params={params}
          basePath={BASE_PATH}
          searchPlaceholder={copy.searchPlaceholder}
          selects={[
            {
              key: "role",
              label: copy.filterRole,
              value: typeof params.role === "string" ? params.role : "",
              options: ROLES.map((role) => ({ value: role, label: roles[role] })),
            },
            {
              key: "zone",
              label: copy.filterZone,
              value: typeof params.zone === "string" ? params.zone : "",
              options: zoneOptions.map((zone) => ({
                value: String(zone.id),
                label: zone.name,
              })),
            },
            {
              key: "supervisor",
              label: copy.filterSupervisor,
              value: typeof params.supervisor === "string" ? params.supervisor : "",
              options: supervisorOptions.map((supervisor) => ({
                value: String(supervisor.id),
                label: supervisor.name,
              })),
            },
            ...(can(user.role, "companies.read")
              ? [
                  {
                    key: "company",
                    label: copy.filterCompany,
                    value: typeof params.company === "string" ? params.company : "",
                    options: companyOptions.map((company) => ({
                      value: String(company.id),
                      label: company.nameAr,
                    })),
                  },
                ]
              : []),
          ]}
          texts={[
            {
              key: "department",
              label: copy.filterDepartment,
              value: typeof params.department === "string" ? params.department : "",
            },
            {
              key: "city",
              label: copy.filterCity,
              value: typeof params.city === "string" ? params.city : "",
            },
          ]}
        />
      </div>

      {showBulk ? (
        <BulkActions
          deactivatable={deactivatable}
          reactivatable={reactivatable}
          assignable={assignable}
          smsable={smsable}
          emailable={emailable}
          notifiable={notifiable}
          zoneOptions={zoneOptions}
          canDeactivate={canDeactivate}
          canAssignZones={canAssignZones}
          canMessage={canMessage}
          numerals={user.numeralSystem}
        >
          {table}
        </BulkActions>
      ) : (
        table
      )}

      {/* A no-JS escape for paging past the default page size, since the filter
          bar's select handlers are the only other way to change it. */}
      {result.pageCount > 1 && pageSize === DEFAULT_PAGE_SIZE ? (
        <p className="mt-3 text-xs text-n6">
          <Link
            to={`${BASE_PATH}${buildQuery(params, { size: 100, page: null })}`}
            className="font-semibold text-orange-700 hover:opacity-80"
          >
            {copy.showMore}
          </Link>
        </p>
      ) : null}
    </div>
  );
}
